import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  Upload, 
  Download, 
  Mail, 
  Trophy, 
  Flag,
  Globe,
  Lock,
  ChevronLeft,
  Loader2,
  Trash2,
  ExternalLink,
  Copy
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChampionshipDetail() {
  const { championshipId } = useParams();
  const fileInputRef = useRef(null);
  const [championship, setChampionship] = useState(null);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedRace, setSelectedRace] = useState(1);
  const [includeAttendance, setIncludeAttendance] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [deleteRaceNum, setDeleteRaceNum] = useState(null);
  const [emailForm, setEmailForm] = useState({
    subject: '',
    message: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchChampionship = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API}/championships/${championshipId}`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setChampionship(data);
        // Initialize attendance switch based on championship config
        if (data.use_attendance_bonus) {
          setIncludeAttendance(true);
        }
      }
    } catch (error) {
      console.error('Error fetching championship:', error);
    }
  };

  const fetchStandings = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(
        `${API}/championships/${championshipId}/standings?include_attendance=${includeAttendance}`,
        { credentials: 'include', headers }
      );
      if (response.ok) {
        const data = await response.json();
        setStandings(data.standings || []);
      }
    } catch (error) {
      console.error('Error fetching standings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChampionship();
    fetchStandings();
  }, [championshipId]);

  useEffect(() => {
    if (championship) {
      fetchStandings();
    }
  }, [includeAttendance]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(
        `${API}/championships/${championshipId}/races/${selectedRace}/upload`,
        {
          method: 'POST',
          credentials: 'include',
          headers,
          body: formData
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al subir resultados');
      }

      const result = await response.json();
      toast.success(`${result.results_added} resultados subidos para Carrera ${selectedRace}`);
      fetchStandings();
    } catch (error) {
      toast.error(String(error?.message || 'Error'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteRace = async () => {
    if (!deleteRaceNum) return;
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(
        `${API}/championships/${championshipId}/races/${deleteRaceNum}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers
        }
      );

      if (!response.ok) {
        throw new Error('Error al eliminar resultados');
      }

      toast.success(`Resultados de Carrera ${deleteRaceNum} eliminados`);
      setDeleteRaceNum(null);
      fetchStandings();
    } catch (error) {
      toast.error(String(error?.message || 'Error'));
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(
        `${API}/championships/${championshipId}/pdf?include_attendance=${includeAttendance}`,
        { credentials: 'include', headers }
      );

      if (!response.ok) {
        throw new Error('Error al generar PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${championship?.name || 'clasificacion'}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Clasificación descargada');
    } catch (error) {
      toast.error(String(error?.message || 'Error'));
    }
  };

  const handleDownloadRaceTemplate = async () => {
    try {
      const response = await fetch(`${API}/race-results-template`);
      if (!response.ok) {
        throw new Error('Error al descargar plantilla');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_resultados_carrera.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Plantilla Excel descargada');
    } catch (error) {
      toast.error(String(error?.message || 'Error'));
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };
      
      const response = await fetch(
        `${API}/championships/${championshipId}/send-email`,
        {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({
            championship_id: championshipId,
            subject: emailForm.subject,
            message: emailForm.message
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al enviar emails');
      }

      const result = await response.json();
      toast.success(`Emails enviados: ${result.sent_count}/${result.total_pilots}`);
      setEmailDialogOpen(false);
      setEmailForm({ subject: '', message: '' });
    } catch (error) {
      toast.error(String(error?.message || 'Error'));
    } finally {
      setSendingEmail(false);
    }
  };

  const copyPublicUrl = () => {
    const url = `${window.location.origin}/clasificacion/${championshipId}`;
    navigator.clipboard.writeText(url);
    toast.success('Enlace copiado');
  };

  const getRaceHasResults = (raceNum) => {
    return standings.some(s => s.race_results[raceNum - 1] !== null);
  };

  // Get total races from championship or default to 4
  const totalRaces = championship?.total_races || 4;
  const racesToCount = championship?.races_to_count || 3;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="championship-detail-page">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <Link 
            to="/campeonatos" 
            className="inline-flex items-center text-muted-foreground hover:text-white text-sm"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Volver a Campeonatos
          </Link>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Trophy className="w-8 h-8 text-primary" />
              <div>
                <h1 className="font-heading font-black text-2xl uppercase">
                  {championship?.name}
                </h1>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{totalRaces} pruebas</span>
                  <span>•</span>
                  <span>{racesToCount} cuentan</span>
                  <span>•</span>
                  {championship?.is_public ? (
                    <>
                      <Globe className="w-4 h-4 text-success" />
                      <span>Pública</span>
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      <span>Privada</span>
                    </>
                  )}
                  {championship?.is_public && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyPublicUrl}
                      data-testid="copy-public-url-btn"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copiar Enlace
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                onClick={handleDownloadPDF}
                className="border-white/10"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Descargar PDF
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setEmailForm({
                    subject: `Clasificación ${championship?.name}`,
                    message: 'Aquí tienes la clasificación actualizada del campeonato.'
                  });
                  setEmailDialogOpen(true);
                }}
                className="border-white/10"
                data-testid="send-email-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Enviar por Email
              </Button>
              {championship?.is_public && (
                <Button 
                  asChild
                  variant="outline"
                  className="border-white/10"
                  data-testid="view-public-btn"
                >
                  <a 
                    href={`/clasificacion/${championshipId}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Ver Pública
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Upload Section */}
        <Card className="bg-card/50 border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-lg uppercase">
              Subir Resultados de Carrera
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
              <div className="space-y-2">
                <Label>Seleccionar Carrera</Label>
                <Tabs 
                  value={selectedRace.toString()} 
                  onValueChange={(v) => setSelectedRace(parseInt(v))}
                >
                  <TabsList className="bg-muted flex-wrap">
                    {Array.from({ length: totalRaces }, (_, i) => i + 1).map((num) => (
                      <TabsTrigger 
                        key={num} 
                        value={num.toString()}
                        className="relative"
                        data-testid={`race-tab-${num}`}
                      >
                        C{num}
                        {getRaceHasResults(num) && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-success rounded-full" />
                        )}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
              <div className="flex flex-wrap gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  data-testid="race-file-input"
                />
                <Button
                  variant="outline"
                  onClick={handleDownloadRaceTemplate}
                  className="border-white/10"
                  data-testid="download-race-template-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Plantilla Excel
                </Button>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-race-btn"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Subir Excel C{selectedRace}
                </Button>
                {getRaceHasResults(selectedRace) && (
                  <Button
                    variant="outline"
                    className="border-white/10"
                    onClick={() => setDeleteRaceNum(selectedRace)}
                    data-testid="delete-race-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2 text-destructive" />
                    Eliminar C{selectedRace}
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Formato Excel: Columna A = licencia, Columna B = posicion
            </p>
          </CardContent>
        </Card>

        {/* Attendance Toggle */}
        <Card className="bg-card/50 border-white/10">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Puntos de Asistencia</Label>
                <p className="text-xs text-muted-foreground">
                  {championship?.use_attendance_bonus && championship?.attendance_model_name 
                    ? `Modelo: ${championship.attendance_model_name}`
                    : "Modelo AECAR: +150 (4 carr.) | +100 (3 carr.) | +50 (2 carr.)"
                  }
                </p>
                {championship?.use_attendance_bonus && (
                  <p className="text-xs text-primary mt-1">
                    Este campeonato tiene asistencia activada por defecto
                  </p>
                )}
              </div>
              <Switch
                checked={includeAttendance}
                onCheckedChange={setIncludeAttendance}
                data-testid="attendance-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Standings Table */}
        <Card className="bg-card/50 border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
              <Flag className="w-5 h-5 text-primary" />
              Clasificación General
            </CardTitle>
          </CardHeader>
          <CardContent>
            {standings.length === 0 ? (
              <div className="text-center py-12">
                <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">
                  No hay resultados. Sube los Excel de las carreras.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="standings-table">
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="w-12">Pos</TableHead>
                      <TableHead>Piloto</TableHead>
                      {Array.from({ length: totalRaces }, (_, i) => (
                        <TableHead key={i} className="text-center">C{i + 1}</TableHead>
                      ))}
                      <TableHead className="text-center hidden md:table-cell">Total</TableHead>
                      <TableHead className="text-center hidden md:table-cell">Desc.</TableHead>
                      {includeAttendance && (
                        <TableHead className="text-center hidden lg:table-cell">Asist.</TableHead>
                      )}
                      <TableHead className="text-right">Final</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {standings.map((entry) => {
                      // Calculate which points are discarded (the lowest ones)
                      const numDiscards = totalRaces - racesToCount;
                      const pointsWithIndex = entry.race_points.map((pts, idx) => ({ idx, pts }));
                      const sortedByPoints = [...pointsWithIndex].sort((a, b) => a.pts - b.pts);
                      const discardedIndices = new Set(sortedByPoints.slice(0, numDiscards).map(x => x.idx));
                      
                      return (
                        <TableRow 
                          key={entry.license_number}
                          className={`border-white/5 ${
                            entry.position === 1 ? 'podium-1' :
                            entry.position === 2 ? 'podium-2' :
                            entry.position === 3 ? 'podium-3' : ''
                          }`}
                          data-testid={`standing-row-${entry.position}`}
                        >
                          <TableCell className="font-bold text-lg">
                            {entry.position}º
                          </TableCell>
                          <TableCell className="font-medium">
                            {entry.pilot_name}
                            {entry.pilot_found === false && (
                              <span className="block text-xs text-red-400 italic" data-testid={`pilot-not-found-${entry.position}`}>
                                Piloto no encontrado
                              </span>
                            )}
                          </TableCell>
                          {entry.race_points.slice(0, totalRaces).map((pts, idx) => (
                            <TableCell 
                              key={idx} 
                              className={`text-center font-mono text-sm ${
                                discardedIndices.has(idx)
                                  ? 'line-through text-muted-foreground' 
                                  : entry.race_results[idx] === null 
                                    ? 'text-muted-foreground' 
                                    : ''
                            }`}
                          >
                            {entry.race_results[idx] !== null ? `${pts} (${entry.race_results[idx]}º)` : '-'}
                          </TableCell>
                        ))}
                        <TableCell className="text-center hidden md:table-cell font-mono text-sm text-muted-foreground">
                          {entry.total_points}
                        </TableCell>
                        <TableCell className="text-center hidden md:table-cell font-mono text-sm text-muted-foreground line-through">
                          -{entry.discarded_points}
                        </TableCell>
                        {includeAttendance && (
                          <TableCell className="text-center hidden lg:table-cell font-mono text-sm text-secondary">
                            +{entry.attendance_bonus}
                          </TableCell>
                        )}
                        <TableCell className="text-right font-mono font-bold text-primary text-lg">
                          {entry.final_points}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Email Dialog */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">
                Enviar Clasificación por Email
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSendEmail} className="space-y-4">
              <div className="space-y-2">
                <Label>Asunto</Label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  required
                  className="bg-black/20 border-white/10"
                  data-testid="email-subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Mensaje</Label>
                <Textarea
                  value={emailForm.message}
                  onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                  required
                  className="bg-black/20 border-white/10"
                  data-testid="email-message-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Se enviará a todos los pilotos con email en la clasificación.
              </p>
              <Button 
                type="submit" 
                className="w-full"
                disabled={sendingEmail}
                data-testid="send-email-submit-btn"
              >
                {sendingEmail ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Enviar Emails
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Race Confirmation */}
        <AlertDialog open={!!deleteRaceNum} onOpenChange={() => setDeleteRaceNum(null)}>
          <AlertDialogContent className="bg-card border-white/10">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading uppercase">
                Eliminar Resultados
              </AlertDialogTitle>
              <AlertDialogDescription>
                ¿Estás seguro de eliminar los resultados de la Carrera {deleteRaceNum}?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-white/10">Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteRace}
                className="bg-destructive hover:bg-destructive/90"
                data-testid="confirm-delete-race-btn"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
